import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Pressable, Platform, useWindowDimensions, FlatList, StyleSheet, ScrollView } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Checkbox, Label, Separator, H4, SizableText, Text, View } from 'tamagui';
import IntegrationComponent from './components/IntegrationComponent';
import IntegrationRequestHeader from '@components/IntegrationRequestHeader';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { useQuery } from '@tanstack/react-query';
import apis from '@api/api';
import AnimatedLottieView from '@utils/lottieAnimation';
import GlobalTutorial from '@components/TutorialComponent/GlobalTutorial';
import * as Animatable from 'react-native-animatable';
import { Image } from 'expo-image';
import { BlurView, Card } from '@constants/Themed';
import { useTranslation } from 'react-i18next';
import { selectUser } from '@reduxLocal/selectors/userSelectors';
import { YStack, XStack, Popover, Select, Adapt, Sheet } from 'tamagui';
import { useDeviceType } from '@hooks/useDeviceType';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { AntDesign, MaterialCommunityIcons } from '@expo/vector-icons';
import getDaysAgo from '@utils/getDaysAgo';
import handleImage from './components/handleImage';
import { MotiView } from 'moti';
import { useRoute } from '@react-navigation/native';
import DatabaseConnectionCard from '../DataScreen/components/DatabaseConnectionCard';
import DatabaseConnectionForm from '../DataScreen/components/DatabaseConnectionForm';
import SchemaImportModal, { SchemaImportModalHandle } from '@components/modals/SchemaImportModal/SchemaImportModal';
import useGlobalUser from '@hooks/getGlobalUser';
import { useRef } from 'react';

const departSelectList = [
  { label: "Sales", value: "sales" },
  { label: "Marketing", value: "marketing" },
  { label: "Support", value: "support" },
];

const IntegrationScreen = () => {
  const { t } = useTranslation();
  const route = useRoute();
  const { authentications } = selectUser();
  const [data, setData] = useState(null); // For the selected integration
  const [searchQuery, setSearchQuery] = useState('');
  const [regexError, setRegexError] = useState('');
  const { width, height } = useWindowDimensions();
  const { isTablet } = useDeviceType();
  const [filters, setFilters] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const theme = themeSelector();
  const [wideView, setWideView] = useState(false);
  const [databaseConnections, setDatabaseConnections] = useState([]);
  const [showDatabaseForm, setShowDatabaseForm] = useState(false);
  const [editingConnection, setEditingConnection] = useState(null);
  const schemaImportRef = useRef<SchemaImportModalHandle>(null);
  const selectedId = useGlobalUser();

  // Handle navigation params to populate search bar
  useEffect(() => {
    const params = (route.params as any);
    if (params?.integrationName) {
      setSearchQuery(params.integrationName);
    }
  }, [route.params]);

  // Load database connections
  useEffect(() => {
    if (selectedId) {
      loadDatabaseConnections();
    }
  }, [selectedId]);

  const loadDatabaseConnections = useCallback(async () => {
    try {
      const connections = await apis.getDatabaseConnections(undefined);
      setDatabaseConnections(connections || []);
    } catch (error) {
      console.error('Error loading database connections:', error);
    }
  }, []);
  // 1. Fetch data with react-query and use it as the single source of truth.
  const { data: originalIntegrationData, isLoading } = useQuery({
    queryKey: ['integrationData'],
    queryFn: () => apis.getIntegrations(),
  });

  // __DEV__: Inject test integration with OpenAPI spec for testing spec-driven renderer
  const testIntegrationData = useMemo(() => {
    if (!__DEV__) return originalIntegrationData;

    const testSpec = {
      openapi: "3.0.1",
      info: { title: "Test Integration", version: "1.0.0" },
      paths: {
        "/profile": {
          get: {
            summary: "Get user profile",
            "x-ui": {
              capability: "read_profile",
              renders: "card",
              priority: 10,
              surface: "options"
            },
            responses: {
              "200": {
                description: "Profile",
                content: {
                  "application/json": {
                    schema: {
                      type: "object",
                      properties: {
                        id: { type: "string" },
                        name: { type: "string" },
                        image: { type: "string" },
                        email: { type: "string", format: "email" },
                        title: { type: "string" },
                        bio: { type: "string" }
                      },
                      required: ["id", "name"]
                    }
                  }
                }
              }
            }
          }
        },
        "/analytics/summary": {
          get: {
            summary: "Get analytics summary",
            "x-ui": {
              capability: "read_analytics",
              renders: "card",
              priority: 8,
              surface: "dashboard"
            },
            responses: {
              "200": {
                description: "Summary",
                content: {
                  "application/json": {
                    schema: {
                      type: "object",
                      properties: {
                        total: { type: "number" },
                        change: { type: "number" },
                        period: { type: "string" },
                        metrics: { type: "object" }
                      },
                      required: ["total"]
                    }
                  }
                }
              }
            }
          }
        },
        "/posts": {
          post: {
            summary: "Create post",
            "x-ui": {
              capability: "create_post",
              renders: "form",
              priority: 6,
              surface: "options"
            },
            requestBody: {
              required: true,
              content: {
                "application/json": {
                  schema: {
                    type: "object",
                    properties: {
                      content: { type: "string" },
                      media: { type: "array", items: { type: "string" } },
                      visibility: { type: "string", enum: ["public", "private"] }
                    },
                    required: ["content"]
                  }
                }
              }
            },
            responses: {
              "201": { description: "Created" }
            }
          }
        }
      }
    };

    const testIntegration = {
      _id: "__test_spec_driven__",
      id: "__test_spec_driven__",
      title: "🧪 Spec-Driven Test (DEV)",
      meta: "spec-driven-test",
      description: "Test integration for spec-driven rendering. Shows ProfileCard, AnalyticsSummary, and PostComposer components.",
      openAPISpec: {
        rawSpec: testSpec,
        enrichedSpec: testSpec, // Already has x-ui metadata
        version: "1.0.0",
        status: "enriched",
        enrichmentMetadata: {
          inferredAt: new Date().toISOString(),
          inferenceMethod: "test"
        },
        refinementHistory: [],
        endpointVersions: {},
        partialUpdates: []
      }
    };

    if (__DEV__) {
      console.log('🧪 Test integration injected:', testIntegration);
    }

    // Inject test integration at the beginning of the array
    if (originalIntegrationData && Array.isArray(originalIntegrationData)) {
      // Remove test integration if it already exists (to avoid duplicates)
      const filtered = originalIntegrationData.filter(
        (item: any) => item._id !== "__test_spec_driven__"
      );
      return [testIntegration, ...filtered];
    }

    return originalIntegrationData;
  }, [originalIntegrationData]);

  // Extract unique regions from integration data
  const availableRegions = useMemo(() => {
    const dataSource = __DEV__ ? testIntegrationData : originalIntegrationData;
    if (!dataSource || !Array.isArray(dataSource)) return [];

    const regionSet = new Set<string>();
    dataSource.forEach((item: any) => {
      if (item.regions && Array.isArray(item.regions)) {
        item.regions.forEach((region: string) => {
          if (region && typeof region === 'string') {
            regionSet.add(region);
          }
        });
      }
    });

    return Array.from(regionSet).sort();
  }, [originalIntegrationData, testIntegrationData]);

  // 2. Derive the filtered data using useMemo. This is more efficient and declarative.
  // It automatically recalculates when the original data, search query, or region filter changes.
  const filteredData = useMemo(() => {
    const dataSource = __DEV__ ? testIntegrationData : originalIntegrationData;
    if (!dataSource) return [];

    let filtered = dataSource;

    // Apply region filter
    if (selectedRegion) {
      filtered = filtered.filter((item: any) => {
        if (!item.regions || !Array.isArray(item.regions)) return false;
        return item.regions.includes(selectedRegion);
      });
    }

    // Apply search query filter
    if (searchQuery) {
      try {
        const regex = new RegExp(searchQuery, 'i');
        setRegexError('');
        filtered = filtered.filter((item: any) => regex.test(item.title));
      } catch (e) {
        setRegexError('Invalid regex');
        return filtered;
      }
    } else {
      setRegexError('');
    }

    return filtered;
  }, [originalIntegrationData, testIntegrationData, searchQuery, selectedRegion]);

const integrationListComponent = useCallback(({ item, index }) => {
    if (!item) return null;

    // Handle database connection items
    if (item._isDatabaseConnection) {
      return (
        <Animatable.View
          useNativeDriver
          animation={'fadeInUp'}
          delay={index * 50}
          duration={300}
          style={{
            width: 300,
            height: '100%',
          }}
        >
          <DatabaseConnectionCard
            connection={item}
            onEdit={(connection) => {
              setEditingConnection(connection);
              setShowDatabaseForm(true);
            }}
            onDelete={async (connectionId) => {
              try {
                await apis.deleteDatabaseConnection(connectionId);
                loadDatabaseConnections();
              } catch (error) {
                console.error('Error deleting connection:', error);
              }
            }}
            onImportSchema={(connectionId) => {
              schemaImportRef.current?.openModal(connectionId, item.name);
            }}
            onTest={async (connectionId) => {
              await loadDatabaseConnections();
            }}
          />
        </Animatable.View>
      );
    }

    const colors = {
      background: theme ? '#ffffff' : '#374151',
      cardBackground: theme ? '#f8fafc' : '#4b5563',
      text: theme ? '#1f2937' : '#f9fafb',
      textSecondary: theme ? '#6b7280' : '#9ca3af',
      border: theme ? '#e5e7eb' : '#4b5563',
      accent: '#3b82f6',
      selected: 'rgb(90,192,234)',
    };

    const isSelected = data && (data as any)?._id === item._id;
  // Special handling for Hugging Face (uses API token, stored as 'huggingface')
  const isHuggingFace = item.meta?.toLowerCase().replace('-', '') === 'huggingface' || item.title?.toLowerCase().replace(/\s+/g, '') === 'huggingface';
  // Special handling for PayStack (uses API keys, stored as 'paystack')
  const isPayStack = item.meta?.toLowerCase().replace('-', '') === 'paystack' || item.title?.toLowerCase().replace(/\s+/g, '') === 'paystack';
  const isConnected = isHuggingFace
    ? authentications?.huggingface?.token || authentications?.huggingface?.username
    : isPayStack
    ? authentications?.paystack?.keysStored || authentications?.paystack?.merchantEmail
    : Object.keys(authentications).includes(item.title.toLowerCase());

    return (
      <Animatable.View
        useNativeDriver
        animation={'fadeInUp'}
        delay={index * 50}
        duration={300}
        style={{
          width: 300,
          height: '100%', // Match parent height
        }}
      >
        <Pressable
          style={({ pressed }) => ({
            backgroundColor: isSelected
              ? colors.selected
              : pressed
                ? colors.cardBackground
                : colors.background,
            marginHorizontal: 8,
            marginVertical: 8,
            borderRadius: 12,
            borderWidth: 1,
            borderColor: isSelected ? colors.selected : colors.border,
            padding: 16,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 2,
            opacity: pressed ? 0.8 : 1,
            flex: 1, // Fill available height
            justifyContent: 'space-between', // Distribute content evenly
          })}
          onPress={() => {
            data && (data as any)?._id === item._id ? setData(undefined) : setData(item)
          }}
        >
          <YStack flex={1} justifyContent="space-between">
            {/* Header with title */}
            <YStack gap="$2">
              <XStack justifyContent="space-between" alignItems="flex-start">
                <H4
                  color={isSelected ? '#ffffff' : colors.text}
                  fontSize="$4"
                  fontWeight="600"
                  numberOfLines={2}
                  flex={1}
                >
                  {item?.title}
                </H4>
              </XStack>

              {/* Content section with image and description */}
              <XStack gap="$3" alignItems="flex-start" flex={1}>
                {/* Integration Icon/Logo Container */}
                <YStack alignItems="center" gap="$1">
                  <View style={{
                    width: 60,
                    height: 60,
                    backgroundColor: isSelected ? 'rgba(255,255,255,0.2)' : `${colors.accent}15`,
                    borderRadius: 30,
                    justifyContent: 'center',
                    alignItems: 'center',
                    overflow: 'hidden',
                    borderWidth: 2,
                    borderColor: isSelected ? 'rgba(255,255,255,0.3)' : colors.border,
                  }}>
                    {item.logo ? (
                      <Image
                        source={{ uri: item.logo }}
                        contentFit='contain'
                        style={{
                          width: 50,
                          height: 50,
                          borderRadius: 25,
                        }}
                      />
                    ) : (
                      <Image
                        source={handleImage(item.meta)}
                        contentFit='contain'
                        style={{
                          width: 50,
                          height: 50,
                          borderRadius: 25,
                        }}
                      />
                    )}
                  </View>

                </YStack>

                {/* Description */}
                <YStack flex={1} justifyContent="flex-start" paddingTop="$2">
                  <SizableText
                    color={isSelected ? '#ffffff' : colors.textSecondary}
                    size="$3"
                    numberOfLines={4}
                    lineHeight={18}
                  >
                    {item.description}
                  </SizableText>
                </YStack>
              </XStack>
            </YStack>

            {/* Footer with status indicator */}
            <XStack justifyContent="space-between" alignItems="center" marginTop="$3">
              <XStack gap="$2" alignItems="center">
                <MaterialCommunityIcons
                  name={isConnected ? "check-circle" : "link-variant"}
                  size={14}
                  color={isSelected ? '#ffffff' : (isConnected ? '#10b981' : colors.textSecondary)}
                />
                <SizableText
                  color={isSelected ? '#ffffff' : (isConnected ? '#10b981' : colors.textSecondary)}
                  size="$2"
                >
                  {isConnected ? 'Connected' : 'Available'}
                </SizableText>
              </XStack>

              <MaterialCommunityIcons
                name={isSelected ? "chevron-up" : "chevron-right"}
                size={16}
                color={isSelected ? '#ffffff' : colors.textSecondary}
              />
            </XStack>
          </YStack>
        </Pressable>
      </Animatable.View>
    );
  }, [data, authentications, setData, theme, loadDatabaseConnections]);

  return (
    <DefaultScreen layoutCallback={(layout) => { setWideView(layout.width > 850) }} upperLevelScreen backButton screenTitle={'Integrations'} scrollDisabled searchBar={{
      setSearchState: setSearchQuery,
      searchState: searchQuery,
      // No callback needed, filtering is automatic with useMemo
    }}>
      {regexError && (
        <Text style={{ color: 'red', textAlign: 'center', marginBottom: 8 }}>{regexError}</Text>
      )}

      {/* Regional Filter */}
      {availableRegions.length > 0 && (
        <XStack
          paddingHorizontal="$4"
          paddingVertical="$2"
          gap="$3"
          alignItems="center"
          justifyContent="flex-start"
          marginBottom="$2"
        >
          <SizableText
            size="$3"
            color={theme ? '#1f2937' : '#f9fafb'}
            fontWeight="500"
          >
            Filter by Region:
          </SizableText>
          <View style={{ minWidth: 200 }}>
            <Select
              value={selectedRegion}
              onValueChange={setSelectedRegion}
              size="$3"
            >
              <Select.Trigger
                backgroundColor={theme ? '#ffffff' : '#374151'}
                borderColor={theme ? '#e5e7eb' : '#4b5563'}
                color={theme ? '#1f2937' : '#f9fafb'}
                iconAfter={<MaterialCommunityIcons name="chevron-down" size={16} color={theme ? '#1f2937' : '#f9fafb'} />}
                minWidth={200}
              >
                <Select.Value placeholder="All Regions" />
              </Select.Trigger>
              <Adapt>
                <Sheet modal dismissOnSnapToBottom>
                  <Sheet.Frame>
                    <Sheet.ScrollView>
                      <Adapt.Contents />
                    </Sheet.ScrollView>
                  </Sheet.Frame>
                  <Sheet.Overlay
                    animation="lazy"
                    enterStyle={{ opacity: 0 }}
                    exitStyle={{ opacity: 0 }}
                  />
                </Sheet>
              </Adapt>
              <Select.Content zIndex={200000}>
                <Select.ScrollUpButton
                  alignItems="center"
                  justifyContent="center"
                  position="relative"
                  width="100%"
                  height="$3"
                >
                  <MaterialCommunityIcons name="chevron-up" size={20} />
                </Select.ScrollUpButton>
                <Select.Viewport minHeight={200}>
                  <Select.Group>
                    <Select.Label>Regions</Select.Label>
                    <Select.Item index={0} value="" key="all">
                      <Select.ItemText>All Regions</Select.ItemText>
                      {!selectedRegion && (
                        <Select.ItemIndicator marginLeft="auto">
                          <MaterialCommunityIcons name="check" size={16} />
                        </Select.ItemIndicator>
                      )}
                    </Select.Item>
                    {availableRegions.map((region, index) => (
                      <Select.Item index={index + 1} value={region} key={region}>
                        <Select.ItemText>{region}</Select.ItemText>
                        <Select.ItemIndicator marginLeft="auto">
                          <MaterialCommunityIcons name="check" size={16} />
                        </Select.ItemIndicator>
                      </Select.Item>
                    ))}
                  </Select.Group>
                </Select.Viewport>
                <Select.ScrollDownButton
                  alignItems="center"
                  justifyContent="center"
                  position="relative"
                  width="100%"
                  height="$3"
                >
                  <MaterialCommunityIcons name="chevron-down" size={20} />
                </Select.ScrollDownButton>
              </Select.Content>
            </Select>
          </View>
          {selectedRegion && (
            <Pressable
              onPress={() => setSelectedRegion('')}
              style={{
                paddingHorizontal: 8,
                paddingVertical: 4,
                borderRadius: 6,
                backgroundColor: theme ? '#f3f4f6' : '#4b5563',
              }}
            >
              <XStack alignItems="center" gap="$1">
                <MaterialCommunityIcons
                  name="close-circle"
                  size={16}
                  color={theme ? '#6b7280' : '#9ca3af'}
                />
                <SizableText
                  size="$2"
                  color={theme ? '#6b7280' : '#9ca3af'}
                >
                  Clear
                </SizableText>
              </XStack>
            </Pressable>
          )}
        </XStack>
      )}

      <View style={{ flexDirection: 'column-reverse', flex: 1, paddingBottom: "5%" }}>
        <YStack flex={1} alignSelf="stretch" gap={20}>
          <Animatable.View useNativeDriver animation='bounceInDown' style={{ flex: 5, alignSelf: "stretch" }}>
            {data ? (
              <IntegrationComponent integration={data} />
            ) : (
              <XStack gap="$4" flex={1} minHeight={0}>
                <Card style={{ flex: 2, minHeight: 0, overflow: 'hidden' }}>
                  <ScrollView 
                    style={{ flex: 1 }} 
                    contentContainerStyle={{ padding: 16 }}
                    showsVerticalScrollIndicator={Platform.OS === 'web'}
                    nestedScrollEnabled={true}
                  >
                    <IntegrationRequestHeader 
                      onRequestSubmitted={() => {
                        // Refresh data or show success message
                      }}
                    />
                  </ScrollView>
                </Card>
                  {
                    wideView &&
                <YStack flex={1} minHeight={0}>
                        <GlobalTutorial carouselData={[{
                          title: 'Select an integration',
                          caption: 'to view its details or connect your account',
                          image: <MaterialCommunityIcons name="link-variant" size={64} color={theme ? "#3b82f6" : "#60a5fa"} />
                        }]} />
                </YStack>
                  }
              </XStack>
            )}
          </Animatable.View>
          <MotiView
            animate={{
              minHeight: (searchQuery.length > 0 && !data)
                ? height * 0.85
                : height * 0.20,
              maxHeight: (searchQuery.length > 0 && !data)
                ? height * 0.85
                : height * 0.20,
            }}
            transition={{ type: 'timing', duration: 400 }}
            style={Platform.OS === 'web' ? integrationStyles.motiContainer : undefined}
          >
            <View style={[
              { width: '100%', height: '100%', borderTopLeftRadius: 20, borderTopRightRadius: 20, paddingVertical: 10, position: 'relative', overflow: 'hidden' },
              Platform.OS === 'web' && integrationStyles.glassContainer
            ]}>
              {/* Background Gradient Layer */}
              <LinearGradient
                colors={theme
                  ? ['rgba(200, 162, 200, 0.15)', 'rgba(135, 206, 235, 0.15)', 'rgba(90, 192, 234, 0.1)']
                  : ['rgba(30, 144, 255, 0.2)', 'rgba(90, 192, 234, 0.15)', 'rgba(30, 144, 255, 0.1)']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={StyleSheet.absoluteFill}
              />

              {/* Floating Blur Elements - Web only */}
              {Platform.OS === 'web' && (
                <>
                  <View style={[integrationStyles.floatingBlur, { top: '10%', left: '10%' }]} />
                  <View style={[integrationStyles.floatingBlur, { bottom: '10%', right: '10%' }]} />
                </>
              )}

              {/* Blur Overlay for Glass Effect - Web only */}
              {Platform.OS === 'web' && (
                <View style={integrationStyles.blurOverlay} />
              )}

              {/* Content Container */}
              <View style={[
                { flex: 1, position: 'relative', zIndex: 3 },
                Platform.OS === 'web' && integrationStyles.contentContainer
              ]}>
                {isLoading ? <Text>Loading...</Text> : (
                  <FlatList
                    horizontal
                    data={[
                      ...databaseConnections.map(conn => ({ ...conn, _isDatabaseConnection: true })),
                      ...(Array.isArray(filteredData) ? filteredData : [])
                    ]}
                    renderItem={integrationListComponent}
                    keyExtractor={(item, index) => item?._isDatabaseConnection ? `db-${item._id}` : String(item?._id || item?.id || index)}
                    contentContainerStyle={{ gap: 15, paddingHorizontal: 30 }}
                    showsHorizontalScrollIndicator={false}
                  />
                )}
              </View>
            </View>
          </MotiView>
        </YStack>
      </View>
      
      {/* Database Connection Form Modal */}
      {showDatabaseForm && (
        <View style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
        }}>
          <View style={{
            width: Platform.OS === 'web' ? 600 : '90%',
            maxWidth: Platform.OS === 'web' ? 800 : '100%',
            maxHeight: Platform.OS === 'web' ? '80%' : '90%',
          }}>
            <DatabaseConnectionForm
              initialData={editingConnection}
              scope="user"
              onSuccess={(connection) => {
                setShowDatabaseForm(false);
                setEditingConnection(null);
                loadDatabaseConnections();
              }}
              onCancel={() => {
                setShowDatabaseForm(false);
                setEditingConnection(null);
              }}
            />
          </View>
        </View>
      )}
      
      {/* Schema Import Modal */}
      <SchemaImportModal
        ref={schemaImportRef}
      />
    </DefaultScreen>
  );
}

// Hero-inspired glass morphism styling
const integrationStyles = StyleSheet.create({
  motiContainer: {
    position: 'relative',
  },
  glassContainer: {
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.2)',
    }),
  },
  floatingBlur: {
    position: 'absolute',
    width: 200,
    height: 200,
    borderRadius: 100,
    backgroundColor: Platform.OS === 'web' ? 'rgba(90, 192, 234, 0.15)' : 'transparent',
    ...(Platform.OS === 'web' && {
      filter: 'blur(60px)',
    }),
    zIndex: 1,
  },
  blurOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: Platform.OS === 'web' ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
    }),
    zIndex: 2,
  },
  contentContainer: {
    ...(Platform.OS === 'web' && {
      backgroundColor: 'transparent',
    }),
  },
});

export default IntegrationScreen;